/* global IC WebSocket */
import { __asyncGenerator, __await } from "tslib";
import { verifySignature, validateEvent } from './event.ts';
import { matchFilters } from './filter.ts';
import { getHex64, getSubscriptionId } from './fakejson.ts';
import { MessageQueue } from './utils.ts';
import IcWebSocket from "ic-websocket-js";
import { IDL } from "@dfinity/candid";
export const AppMessageIdl = IDL.Record({
    'text': IDL.Text,
    'timestamp': IDL.Nat64,
});
export const serializeAppMessage = (message) => {
    return new Uint8Array(IDL.encode([AppMessageIdl], [message]));
};
export const deserializeAppMessage = (bytes) => {
    return IDL.decode([AppMessageIdl], bytes)[0];
};
const newListeners = () => ({
    connect: [],
    disconnect: [],
    error: [],
    notice: [],
    auth: [],
});
export function relayInit(gateway_url, canister_actor, canister_id, ic_url, local, persist_key, options = {}) {
    let { listTimeout = 3000, getTimeout = 3000, countTimeout = 3000 } = options;
    var ws;
    var openSubs = {};
    var listeners = newListeners();
    var subListeners = {};
    var pubListeners = {};
    var connectionPromise;
    async function connect() {
        try {
            ws = new IcWebSocket(gateway_url, undefined, {
                canisterActor: canister_actor,
                canisterId: canister_id,
                networkUrl: ic_url,
                localTest: true,
                persistKey: false,
            });
        }
        catch (err) {
            console.error(err);
        }
        ws.onopen = (event) => {
            listeners.connect.forEach(cb => cb());
        };
        ws.onerror = (error) => {
            connectionPromise = undefined;
            listeners.error.forEach(cb => cb());
        };
        ws.onclose = async () => {
            connectionPromise = undefined;
            listeners.disconnect.forEach(cb => cb());
        };
        let incomingMessageQueue = new MessageQueue();
        let handleNextInterval;
        ws.onmessage = async (event) => {
            incomingMessageQueue.enqueue(event.data);
            if (!handleNextInterval) {
                handleNextInterval = setInterval(handleNext, 0);
            }
        };
        function handleNext() {
            var _a, _b, _c;
            if (incomingMessageQueue.size === 0) {
                clearInterval(handleNextInterval);
                handleNextInterval = null;
                return;
            }
            let data = incomingMessageQueue.dequeue();
            if (data === null) {
                return;
            }
            const decoded = deserializeAppMessage(data);
            // get the message to be in the expected format for this crazy (unnecessary?) thing below
            const fake_json = decoded.text
                .replace(/"action":|"subscription_id":|"event":|"message:"/g, "")
                .replace(/^{/, "[")
                .replace(/}$/, "]");
            let subid = getSubscriptionId(fake_json);
            if (subid) {
                let so = openSubs[subid];
                if (so && so.alreadyHaveEvent && so.alreadyHaveEvent(getHex64(fake_json, 'id'), canister_id)) {
                    return;
                }
            }
            try {
                let data = JSON.parse(fake_json);
                // we won't do any checks against the data since all failures (i.e. invalid messages from relays)
                // will naturally be caught by the encompassing try..catch block
                switch (data[0]) {
                    case 'EVENT': {
                        let id = data[1];
                        let event = data[2];
                        if (validateEvent(event) &&
                            openSubs[id] &&
                            (openSubs[id].skipVerification || verifySignature(event)) &&
                            matchFilters(openSubs[id].filters, event)) {
                            openSubs[id];
                            (((_a = subListeners[id]) === null || _a === void 0 ? void 0 : _a.event) || []).forEach(cb => cb(event));
                        }
                        return;
                    }
                    case 'COUNT':
                        let id = data[1];
                        let payload = data[2];
                        if (openSubs[id]) {
                            ;
                            (((_b = subListeners[id]) === null || _b === void 0 ? void 0 : _b.count) || []).forEach(cb => cb(payload));
                        }
                        return;
                    case 'EOSE': {
                        let id = data[1];
                        if (id in subListeners) {
                            subListeners[id].eose.forEach(cb => cb());
                            subListeners[id].eose = []; // 'eose' only happens once per sub, so stop listeners here
                        }
                        return;
                    }
                    case 'OK': {
                        let id = data[1];
                        let ok = data[2];
                        let reason = data[3] || '';
                        if (id in pubListeners) {
                            let { resolve, reject } = pubListeners[id];
                            if (ok)
                                resolve(null);
                            else
                                reject(new Error(reason));
                        }
                        return;
                    }
                    case 'NOTICE':
                        let notice = data[1];
                        listeners.notice.forEach(cb => cb(notice));
                        return;
                    case 'AUTH': {
                        let challenge = data[1];
                        (_c = listeners.auth) === null || _c === void 0 ? void 0 : _c.forEach(cb => cb(challenge));
                        return;
                    }
                }
            }
            catch (err) {
                return;
            }
        }
    }
    async function trySend(params) {
        let message = {
            text: JSON.stringify(params),
            timestamp: BigInt(Date.now())
        };
        await ws.send(serializeAppMessage(message));
    }
    const sub = (filters, { verb = 'REQ', skipVerification = false, alreadyHaveEvent = null, id = Math.random().toString().slice(2), } = {}) => {
        let subid = id;
        openSubs[subid] = {
            id: subid,
            filters,
            skipVerification,
            alreadyHaveEvent,
        };
        trySend([verb, subid]);
        let subscription = {
            sub: (newFilters, newOpts = {}) => sub(newFilters || filters, {
                skipVerification: newOpts.skipVerification || skipVerification,
                alreadyHaveEvent: newOpts.alreadyHaveEvent || alreadyHaveEvent,
                id: subid,
            }),
            unsub: async () => {
                delete openSubs[subid];
                delete subListeners[subid];
                await trySend(['CLOSE', subid]);
            },
            on: (type, cb) => {
                subListeners[subid] = subListeners[subid] || {
                    event: [],
                    count: [],
                    eose: [],
                    error: [],
                };
                subListeners[subid][type].push(cb);
            },
            off: (type, cb) => {
                let listeners = subListeners[subid];
                let idx = listeners[type].indexOf(cb);
                if (idx >= 0)
                    listeners[type].splice(idx, 1);
            },
            get events() {
                return eventsGenerator(subscription);
            },
        };
        return subscription;
    };
    function _publishEvent(event, type) {
        return new Promise(async (resolve, reject) => {
            if (!event.id) {
                reject(new Error(`event ${event} has no id`));
                return;
            }
            let id = event.id;
            await trySend([type, event]);
            pubListeners[id] = { resolve, reject };
        });
    }
    return {
        gateway_url,
        canister_actor,
        canister_id,
        ic_url,
        local,
        persist_key,
        sub,
        on: (type, cb) => {
            listeners[type].push(cb);
        },
        off: (type, cb) => {
            let index = listeners[type].indexOf(cb);
            if (index !== -1)
                listeners[type].splice(index, 1);
        },
        list: (filters, opts) => new Promise(resolve => {
            let s = sub(filters, opts);
            let events = [];
            let timeout = setTimeout(() => {
                s.unsub();
                resolve(events);
            }, listTimeout);
            s.on('eose', () => {
                s.unsub();
                clearTimeout(timeout);
                resolve(events);
            });
            s.on('event', event => {
                events.push(event);
            });
        }),
        get: (filter, opts) => new Promise(resolve => {
            let s = sub([filter], opts);
            let timeout = setTimeout(() => {
                s.unsub();
                resolve(null);
            }, getTimeout);
            s.on('event', event => {
                s.unsub();
                clearTimeout(timeout);
                resolve(event);
            });
        }),
        count: (filters) => new Promise(resolve => {
            let s = sub(filters, Object.assign(Object.assign({}, sub), { verb: 'COUNT' }));
            let timeout = setTimeout(() => {
                s.unsub();
                resolve(null);
            }, countTimeout);
            s.on('count', (event) => {
                s.unsub();
                clearTimeout(timeout);
                resolve(event);
            });
        }),
        async publish(event) {
            await _publishEvent(event, 'EVENT');
        },
        async auth(event) {
            await _publishEvent(event, 'AUTH');
        },
        connect,
        close() {
            listeners = newListeners();
            subListeners = {};
            pubListeners = {};
            ws.close();
        },
    };
}
export function eventsGenerator(sub) {
    return __asyncGenerator(this, arguments, function* eventsGenerator_1() {
        let nextResolve;
        const eventQueue = [];
        const pushToQueue = (event) => {
            if (nextResolve) {
                nextResolve(event);
                nextResolve = undefined;
            }
            else {
                eventQueue.push(event);
            }
        };
        sub.on('event', pushToQueue);
        try {
            while (true) {
                if (eventQueue.length > 0) {
                    yield yield __await(eventQueue.shift());
                }
                else {
                    const event = yield __await(new Promise(resolve => {
                        nextResolve = resolve;
                    }));
                    yield yield __await(event);
                }
            }
        }
        finally {
            sub.off('event', pushToQueue);
        }
    });
}
